import { requestPrint } from '@/libs/bixolon/bxlcommon'
import { checkPrinterStatus, cutPaper, getPosData, setCharacterset, setPosId, printText } from '@/libs/bixolon/bxlpos'
import { printLine, printTotals, printSeparator, wrapTextWithPrefix, printLineWithSpacing } from '@/libs/bixolon/printer'
import state from '@/state'
import { df } from '@/plugins/firebase'
import * as JSPM from 'jsprintmanager'
import EventBus from '@/plugins/event-bus'

// ============================================================================
// PRINT FORMAT MANAGEMENT
// ============================================================================

/**
 * Returns default print format settings that match current hardcoded values
 * This ensures backward compatibility - printers without custom settings
 * will print exactly as they do now
 */
export const getDefaultPrintFormat = () => {
  return {
    header: { fontType: 'A', fontSize: 2, bold: true, alignment: 1, lineSpacing: 1 },
    metadata: { fontType: 'A', fontSize: 0, bold: true, alignment: 0, lineSpacing: 1 },
    courses: { fontType: 'A', fontSize: 0, bold: true, alignment: 0, lineSpacing: 1 },
    items: { fontType: 'A', fontSize: 0, bold: true, alignment: 0, lineSpacing: 1 },
    menuItems: { fontType: 'A', fontSize: 0, bold: false, alignment: 0, lineSpacing: 1 },
    notes: { fontType: 'A', fontSize: 0, bold: false, alignment: 0, lineSpacing: 1 },
    footer: { fontType: 'A', fontSize: 0, bold: true, alignment: 0, lineSpacing: 1 }
  }
}

/**
 * Loads print format for a specific printer from Firestore
 * Falls back to default format if no custom format exists
 *
 * @param {string} printerId - The printer ID
 * @param {string} locationId - The location ID
 * @returns {Promise<Object>} Print format object
 */
export const loadPrintFormat = async (printerId, locationId) => {
  try {
    if (!printerId || !locationId) {
      console.warn('[order.js] Missing printerId or locationId, using default format')
      return getDefaultPrintFormat()
    }

    const docRef = df.doc(`location_printers/${locationId}/printers/${printerId}`)
    const doc = await docRef.get()

    if (doc.exists) {
      const data = doc.data()
      if (data.print_format) {
        console.log('[order.js] Loaded custom print format for printer:', printerId)

        // Ensure backwards compatibility - merge with default format
        const defaultFormat = getDefaultPrintFormat()
        const format = data.print_format

        // Add missing sections from default (e.g., 'notes' for old formats)
        Object.keys(defaultFormat).forEach(section => {
          if (!format[section]) {
            format[section] = defaultFormat[section]
          }
        })

        // Add missing properties within sections (e.g., lineSpacing)
        Object.keys(format).forEach(section => {
          if (format[section] && defaultFormat[section]) {
            Object.keys(defaultFormat[section]).forEach(prop => {
              if (!format[section].hasOwnProperty(prop)) {
                format[section][prop] = defaultFormat[section][prop]
              }
            })
          }
        })

        return format
      }
    }

    console.log('[order.js] No custom format found, using default for printer:', printerId)
    return getDefaultPrintFormat()
  } catch (error) {
    console.error('[order.js] Error loading print format:', error)
    return getDefaultPrintFormat()
  }
}

/**
 * Maps format section settings to ESC/POS options
 *
 * @param {Object} formatSection - Section format (fontType, fontSize, bold, alignment)
 * @param {Object} baseOptions - Base options to extend
 * @param {string} printerLib - Printer library ('UNIVERSAL' or 'BIXOLON')
 * @returns {Object} ESC/POS options
 */
export const getOptionsFromFormat = (formatSection, baseOptions, printerLib) => {
  const options = { ...baseOptions }

  // Font type mapping
  if (printerLib === 'UNIVERSAL') {
    options.font = formatSection.fontType
  } else {
    if (formatSection.fontType === 'A') options.font = 0
    else if (formatSection.fontType === 'B') options.font = 1
    else if (formatSection.fontType === 'C') options.font = 2
  }

  // Font size mapping with finer granularity
  // fontSize 0 → 0x0 (Normal), 1 → 1x0 (Srednji-wider), 2 → 1x1 (Velik), 3 → 2x2 (Veći), 4 → 3x3 (Ekstra)
  const fontSize = formatSection.fontSize || 0
  const scaleMappings = [
    { h: 0, v: 0 }, // 0: Normal (1x1)
    { h: 1, v: 0 }, // 1: Srednji (2x1 - wider only for intermediate size)
    { h: 1, v: 1 }, // 2: Velik (2x2)
    { h: 2, v: 2 }, // 3: Veći (3x3)
    { h: 3, v: 3 }  // 4: Ekstra (4x4)
  ]
  const scaleMapping = scaleMappings[fontSize] || scaleMappings[0]
  options.horizontal = scaleMapping.h
  options.vertical = scaleMapping.v

  // Bold
  options.bold = formatSection.bold ? 1 : 0

  // Alignment (0=left, 1=center, 2=right)
  options.alignment = formatSection.alignment || 0

  // Line spacing (0=normal, 1=larger spacing)
  options.lineSpacing = formatSection.lineSpacing || 0

  return options
}

export const printerInit = (issueID) => {
  checkPrinterStatus()
  setCharacterset(1250)
  setPosId(issueID)
}

function formatQuantity (quantity) {
  // Round to 2 decimal places to avoid floating point errors
  const rounded = Math.round(quantity * 100) / 100
  // If it's a whole number, return without decimals
  if (Number.isInteger(rounded)) {
    return rounded.toString()
  }
  // Otherwise, return with up to 2 decimals, removing trailing zeros
  return rounded.toFixed(2).replace(/\.?0+$/, '')
}

function getUserFriendlyErrorMessage (error) {
  if (!error) return 'Nepoznata greška pri printanju'

  const errorStr = error.toString().toLowerCase()

  if (errorStr.includes('cannot connect to server')) {
    return 'Printer server nije dostupan. Provjerite mrežnu konekciju.'
  } else if (errorStr.includes('no printers')) {
    return 'Printer nije pronađen. Provjerite da li je printer uključen.'
  } else if (errorStr.includes('timeout')) {
    return 'Printer ne odgovara. Provjerite da li je printer aktivan.'
  } else if (errorStr.includes('duplicat')) {
    return 'Dupli print request. Molimo pričekajte i pokušajte ponovno.'
  } else if (errorStr.includes('printer not ready')) {
    return 'Printer nije spreman. Molimo pričekajte i pokušajte ponovno.'
  } else {
    return `Greška pri printanju: ${error}`
  }
}

const viewResult = (result) => {
  if (result === 'Cannot connect to server') {
    alert('Pisač je nedostupan.')
  }
}

export const getTableOrders = async (oldTable, orderId) => {
  let dbOrder
  await df.doc(`location_orders/${state.getPointOfSale().id}/tables/${oldTable.id}`)
    .get()
    .then((tableData) => {
      const table = tableData.data()
      if (table && table.orders && table.orders.length > 0) {
        table.orders.forEach((order, index) => {
          if (order.id === orderId) {
            dbOrder = table.orders[index]
          }
        })
      }
    })

  return dbOrder
}

export const printOrder = async (its, printerData, table = undefined, order = undefined, orgUnit = undefined, otherUnits = undefined, note = undefined, hideLoader = undefined, formatOverride = undefined) => {
  // Printer libraries should be initialized in router beforeEnter hooks
  // This is just a safety check
  if (state.getPrinterLib() === 'UNIVERSAL') {
    if (!window.Neodynamic || !window.Neodynamic.JSESCPOSBuilder) {
      console.warn('⚠️ [order.js] JSESCPOSBuilder not loaded - printer may not work correctly')
    }
  } else if (state.getPrinterLib() !== 'NONE') {
    if (!window.jscpos || !window.jscpos.USBDevice) {
      console.warn('⚠️ [order.js] jscpos not loaded - printer may not work correctly')
    }
  }

  // Load print format (custom or default)
  let printFormat
  if (formatOverride) {
    console.log('[order.js] Using format override for test print')
    printFormat = formatOverride
  } else {
    const locationId = state.getPointOfSale()?.id
    const printerId = printerData.id
    printFormat = await loadPrintFormat(printerId, locationId)
  }

  // Fix za staru konfiguraciju
  if (!['UNIVERSAL', 'NONE'].includes(state.getPrinterLib())) {
    if (!printerData.font) printerData.font = 0
    if (!printerData.size) printerData.size = 0
    if (!printerData.width) printerData.width = '80'
    if (!printerData.line_length) printerData.line_length = 48
    if (!printerData.library) printerData.library = 'BIXOLON'
  }

  let [escpos, doc, printerConfiguration] = [null, null, null]
  let items = []
  items = its
  printerConfiguration = printerData

  // Initialize document based on printer configuration, not global state
  // This ensures test prints work correctly even if state is not set
  console.log('[order.js] Printer library check - library:', printerConfiguration.library)
  console.log('[order.js] window.Neodynamic available:', !!(window.Neodynamic && window.Neodynamic.JSESCPOSBuilder))

  if (printerConfiguration.library === 'UNIVERSAL') {
    if (window.Neodynamic && window.Neodynamic.JSESCPOSBuilder) {
      escpos = window.Neodynamic.JSESCPOSBuilder
      doc = new escpos.Document()
      console.log('[order.js] ✅ UNIVERSAL library initialized successfully')
      console.log('[order.js] doc:', doc, 'escpos:', escpos)
    } else {
      console.error('[order.js] ❌ UNIVERSAL library not available (window.Neodynamic missing)')
      console.error('[order.js] This may happen if printer libraries are not loaded in router hooks')
    }
  } else {
    console.log('[order.js] Using BIXOLON library for printer:', printerConfiguration.name || printerConfiguration.id)
  }

  // Delete items that are menu, and group them in menu
  const menus = {}
  let hasMenu = false

  items.forEach((item, key) => {
    if (item.menuId && item.menuId !== '') {
      if (!menus[item.menuId]) {
        menus[item.menuId] = []
      }
      hasMenu = true
      menus[item.menuId].push(item)

      menus[item.menuId].tags = {}
      menus[item.menuId].tags = item.tags

      delete items[key]
    }
  })

  // NEW: Extract and group items by course (similar to menu handling)
  const courses = {}
  let hasCourses = false
  let courseOrder = []  // Track course printing order

  items.forEach((item, key) => {
    if (item.course_id !== undefined && item.course_id !== null) {
      if (!courses[item.course_id]) {
        courses[item.course_id] = {
          name: item.course_name,
          index: item.course_index,
          items: []
        }
        courseOrder.push(item.course_id)
      }
      hasCourses = true
      courses[item.course_id].items.push(item)

      delete items[key]  // Remove from main items array
    }
  })

  // Sort courseOrder by original course index to maintain display order
  if (hasCourses) {
    courseOrder.sort((a, b) => courses[a].index - courses[b].index)
    console.log('🟢 [order.js] Extracted', courseOrder.length, 'courses for printing')
  }

  const user = state.getCashRegisterUser() && state.getCashRegisterUser().id ? state.getCashRegisterUser() : state.getUser()

  const options = {
    horizontal: 0,
    vertical: 0,
    bold: false,
    invert: false,
    underline: false,
    alignment: 0,
    printerConfiguration: printerConfiguration,
    font: printerConfiguration.font,
    document: doc,
    escpos: escpos
  }

  // Convert font to numeric values for BIXOLON library
  // For UNIVERSAL, keep string values ('A', 'B', 'C')
  if (printerConfiguration.library === 'UNIVERSAL') {
    options.font = printerConfiguration.font
  } else {
    if (printerConfiguration.font === 'A') options.font = 0
    else if (printerConfiguration.font === 'B') options.font = 1
    else if (printerConfiguration.font === 'C') options.font = 2
  }

  function getOptions (option) {
    const modifiedOptions = JSON.parse(JSON.stringify(options))
    Object.keys(option).forEach(key => {
      modifiedOptions[key] = option[key]
    })
    modifiedOptions.document = options.document
    modifiedOptions.escpos = options.escpos
    modifiedOptions.printerConfiguration = options.printerConfiguration
    return modifiedOptions
  }

  console.log('🟢 [order.js] Starting print - hasCourses:', hasCourses, 'library:', printerConfiguration.library)
  console.log('🟢 [order.js] doc:', doc, 'escpos:', escpos)

  // Get printer library for format mapping
  const printerLib = printerConfiguration.library

  if (table && table.name && table.name !== '') {
    console.log('🟢 [order.js] Printing table name:', table.name)
    printLineWithSpacing(`${table.name}`, getOptions(getOptionsFromFormat(printFormat.header, options, printerLib)))
    console.log('🟢 [order.js] Table name printed')
  }

  if (order && order.order_number) {
    console.log('🟢 [order.js] Printing order number:', order.order_number)
    printLineWithSpacing('NARUDŽBA ' + order.order_number, getOptions(getOptionsFromFormat(printFormat.header, options, printerLib)))
  } else {
    console.log('🟢 [order.js] Printing order without number')
    printLineWithSpacing('NARUDŽBA ', getOptions(getOptionsFromFormat(printFormat.header, options, printerLib)))
  }
  console.log('🟢 [order.js] Order header printed')

  // Print separator without \n
  const separatorLine = '-'.repeat(printerConfiguration.line_length)
  if (printerLib === 'UNIVERSAL') {
    options.document.style([options.escpos.FontStyle.Normal])
    options.document.size(0, 0)
    options.document.text(separatorLine, printerConfiguration.code_page ?? 1250)
  } else {
    printText(separatorLine, 0, 0, false, false, false, 'A', 0)
  }

  var date = new Date()

  console.log('🟢 [order.js] Printing operator:', user.name)
  const metadataOptions = getOptionsFromFormat(printFormat.metadata, options, printerLib)
  // Operator uses lineSpacing: 0 - no extra \n, immediately after separator
  printLine(`Operater: ${user.name}`, getOptions({ ...metadataOptions, lineSpacing: 0 }))
  printTotals('Datum: ' + ('0' + date.getDate()).slice(-2) + '.' + ('0' + (date.getMonth() + 1)).slice(-2) + '.' + date.getFullYear(), 'Vrijeme: ' + ('0' + date.getHours()).slice(-2) + ':' + ('0' + date.getMinutes()).slice(-2), getOptionsFromFormat(printFormat.metadata, options, printerLib))

  console.log('🟢 [order.js] Header section complete')
  printSeparator(options)

  // NEW: Print courses if they exist (before regular items)
  if (hasCourses === true) {
    console.log('🟢 [order.js] Printing', courseOrder.length, 'courses')
    courseOrder.forEach((courseId, courseIndex) => {
      if (courseIndex > 0) {
        // Add spacing before separator to separate from last item/comment
        const courseOptions = getOptionsFromFormat(printFormat.courses, options, printerLib)
        printLineWithSpacing('', getOptions(courseOptions))
        printSeparator(options)  // Add separator between courses
      }

      if (courses[courseId] && courses[courseId].items.length > 0) {
        // Print course header with decorative separator (skip "OSTALO")
        const courseName = courses[courseId].name || 'Course ' + courseId
        console.log('🟢 [order.js] Printing course header:', courseName)

        // Only print course header if it's not "OSTALO"
        if (courseName.toUpperCase() !== 'OSTALO') {
          const courseOptions = getOptionsFromFormat(printFormat.courses, { ...options, alignment: 1 }, printerLib)
          printLineWithSpacing(`== ${courseName.toUpperCase()} ==`,
                    getOptions(courseOptions))
          // Add spacing line after course header
          printLineWithSpacing('', getOptions(courseOptions))
        }

        // Print all items in this course
        courses[courseId].items.forEach((item, itemIndex) => {
          console.log('🟢 [order.js] Printing course item:', item.name, 'qty:', item.quantity)

          // Add short centered separator between items (not before first item)
          if (itemIndex > 0) {
            const separatorText = '- - - - -'
            const lineLength = printerConfiguration.line_length
            const startPosition = Math.round(lineLength / 2) - Math.round(separatorText.length / 2)
            const centeredSeparator = ' '.repeat(Math.max(0, startPosition)) + separatorText

            if (printerLib === 'UNIVERSAL') {
              // Print separator without newline - item will add newline before itself
              options.document.style([options.escpos.FontStyle.Normal])
              options.document.size(0, 0)
              options.document.text(centeredSeparator, printerConfiguration.code_page ?? 1250)
            } else {
              // For BIXOLON printer
              printText(centeredSeparator, 0, 0, false, false, false, 'A', 0)
            }
          }

          // Course items use "Stavke" configuration, not "Slijedovi"
          const formatOptions = getOptionsFromFormat(printFormat.items, options, printerLib)
          const quantity = item.quantity || (item.amt ? item.amt / 1000 : 1)
          const quantityPrefix = formatQuantity(quantity) + ' X '
          const prefixLength = quantityPrefix.length
          const indent = ' '.repeat(prefixLength)

          // Wrap item name using proper word-boundary wrapping with font scaling
          const itemName = item.name || 'Unknown item'
          const lines = wrapTextWithPrefix(
            itemName,
            quantityPrefix,
            indent,
            formatOptions.horizontal,
            printerConfiguration.line_length
          )

          // Print first line with quantity prefix
          // Use lineSpacing: 0 to minimize gap after course header or separator
          printLine(quantityPrefix + lines[0].toUpperCase(),
                    getOptions({ ...formatOptions, lineSpacing: 0 }))

          // Print remaining lines with indent that matches prefix length
          for (let i = 1; i < lines.length; i++) {
            printLine(indent + lines[i].toUpperCase(),
                      getOptions({ ...formatOptions, lineSpacing: 0 }))
          }

          // Print item notes if exist (use notes format configuration)
          const notesFormatOptions = getOptionsFromFormat(printFormat.notes, options, printerLib)

          // Print item.notes array (if exists)
          if (item.notes && item.notes.length > 0) {
            item.notes.forEach(note => {
              if (note && note.message) {
                // Find matching note for this order if order exists, otherwise print all notes
                if (!order || note.orderId === order.id) {
                  // Wrap note text with prefix "- " and indent "  "
                  const notePrefix = '- '
                  const noteIndent = '  '
                  const noteLines = wrapTextWithPrefix(
                    note.message,
                    notePrefix,
                    noteIndent,
                    notesFormatOptions.horizontal,
                    printerConfiguration.line_length
                  )

                  // Print first line with "- " prefix (minimal spacing from item)
                  printLine(notePrefix + noteLines[0], getOptions({ ...notesFormatOptions, lineSpacing: 0 }))

                  // Print remaining lines with indent
                  for (let i = 1; i < noteLines.length; i++) {
                    printLine(noteIndent + noteLines[i], getOptions({ ...notesFormatOptions, lineSpacing: 0 }))
                  }
                }
              }
            })
          }

          // Print item.note string (if exists) - from order structure
          if (item.note && item.note.trim() !== '') {
            // Wrap note text with prefix "- " and indent "  "
            const notePrefix = '- '
            const noteIndent = '  '
            const noteLines = wrapTextWithPrefix(
              item.note.trim(),
              notePrefix,
              noteIndent,
              notesFormatOptions.horizontal,
              printerConfiguration.line_length
            )

            // Print first line with "- " prefix (minimal spacing from item)
            printLine(notePrefix + noteLines[0], getOptions({ ...notesFormatOptions, lineSpacing: 0 }))

            // Print remaining lines with indent
            for (let i = 1; i < noteLines.length; i++) {
              printLineWithSpacing(noteIndent + noteLines[i], getOptions(notesFormatOptions))
            }
          }
        })
      }
    })

    // Add separator before remaining items (if any)
    if (items.filter(i => i).length > 0) {
      // Add spacing before separator to separate from last course item/comment
      const itemOptions = getOptionsFromFormat(printFormat.items, options, printerLib)
      printLineWithSpacing('', getOptions(itemOptions))
      printSeparator(options)
    }
  }

  items.forEach((item, index) => {
    // Add short centered separator between items (not before first item)
    if (index > 0) {
      const separatorText = '- - - - -'
      const lineLength = printerConfiguration.line_length
      const startPosition = Math.round(lineLength / 2) - Math.round(separatorText.length / 2)
      const centeredSeparator = ' '.repeat(Math.max(0, startPosition)) + separatorText

      if (printerLib === 'UNIVERSAL') {
        // Print separator without newline - no space after separator
        options.document.style([options.escpos.FontStyle.Normal])
        options.document.size(0, 0)
        options.document.text(centeredSeparator, printerConfiguration.code_page ?? 1250)
      } else {
        // For BIXOLON printer - no newline after separator
        printText(centeredSeparator, 0, 0, false, false, false, 'A', 0)
      }
    }

    const formatOptions = getOptionsFromFormat(printFormat.items, options, printerLib)

    // Quantity can be stored as item.quantity or item.amt (in thousandths)
    const quantity = item.quantity || (item.amt ? item.amt / 1000 : 1)
    const quantityPrefix = formatQuantity(quantity) + ' X '
    const prefixLength = quantityPrefix.length
    const indent = ' '.repeat(prefixLength)

    // Wrap item name using proper word-boundary wrapping with font scaling
    const itemName = item.name
    const lines = wrapTextWithPrefix(
      itemName,
      quantityPrefix,
      indent,
      formatOptions.horizontal,
      printerConfiguration.line_length
    )

    // Print first line with quantity prefix
    // Use lineSpacing: 0 to minimize gap after separator
    printLine(quantityPrefix + lines[0].toUpperCase(), getOptions({ ...formatOptions, lineSpacing: 0 }))

    // Print remaining lines with indent
    for (let i = 1; i < lines.length; i++) {
      printLine(indent + lines[i].toUpperCase(), getOptions({ ...formatOptions, lineSpacing: 0 }))
    }

    // Print item notes (use notes format configuration)
    const notesFormatOptions = getOptionsFromFormat(printFormat.notes, options, printerLib)

    // Print item.notes array (if exists)
    if (item.notes && item.notes.length > 0) {
      item.notes.forEach(note => {
        if (note && note.message) {
          // Find matching note for this order if order exists, otherwise print all notes
          if (!order || note.orderId === order.id) {
            // Wrap note text with prefix "- " and indent "  "
            const notePrefix = '- '
            const noteIndent = '  '
            const noteLines = wrapTextWithPrefix(
              note.message,
              notePrefix,
              noteIndent,
              notesFormatOptions.horizontal,
              printerConfiguration.line_length
            )

            // Print first line with "- " prefix (minimal spacing from item)
            printLine(notePrefix + noteLines[0], getOptions({ ...notesFormatOptions, lineSpacing: 0 }))

            // Print remaining lines with indent
            for (let i = 1; i < noteLines.length; i++) {
              printLineWithSpacing(noteIndent + noteLines[i], getOptions(notesFormatOptions))
            }
          }
        }
      })
    }

    // Print item.note string (if exists) - from order structure
    if (item.note && item.note.trim() !== '') {
      // Wrap note text with prefix "- " and indent "  "
      const notePrefix = '- '
      const noteIndent = '  '
      const noteLines = wrapTextWithPrefix(
        item.note.trim(),
        notePrefix,
        noteIndent,
        notesFormatOptions.horizontal,
        printerConfiguration.line_length
      )

      // Print first line with "- " prefix (minimal spacing from item)
      printLine(notePrefix + noteLines[0], getOptions({ ...notesFormatOptions, lineSpacing: 0 }))

      // Print remaining lines with indent
      for (let i = 1; i < noteLines.length; i++) {
        printLine(noteIndent + noteLines[i], getOptions({ ...notesFormatOptions, lineSpacing: 0 }))
      }
    }
  })

  if (hasMenu === true) {
    Object.keys(menus).forEach((key, kindex) => {
      if (kindex > 0) {
        // Add spacing before separator to separate from previous menu
        const menuOptions = getOptionsFromFormat(printFormat.menuItems, options, printerLib)
        printLineWithSpacing('', getOptions(menuOptions))
        printSeparator(options)
      }

      if (menus[key] && menus[key].length > 0) {
        menus[key].forEach((menuItem, index) => {
          if (index === 0) {
            // Print menu name as item
            const itemFormatOptions = getOptionsFromFormat(printFormat.items, options, printerLib)
            const quantityPrefix = '1 X '
            const prefixLength = quantityPrefix.length
            const indent = ' '.repeat(prefixLength)

            // Wrap menu name using proper word-boundary wrapping with font scaling
            const menuName = menuItem.menuData.name
            const menuNameLines = wrapTextWithPrefix(
              menuName,
              quantityPrefix,
              indent,
              itemFormatOptions.horizontal,
              printerConfiguration.line_length
            )

            // Use lineSpacing: 0 to minimize gap after separator
            printLine(quantityPrefix + menuNameLines[0].toUpperCase(), getOptions({ ...itemFormatOptions, lineSpacing: 0 }))
            for (let i = 1; i < menuNameLines.length; i++) {
              printLineWithSpacing(indent + menuNameLines[i].toUpperCase(), getOptions(itemFormatOptions))
            }
          }

          // Print menu product name and item name with menuItems format
          const menuFormatOptions = getOptionsFromFormat(printFormat.menuItems, options, printerLib)
          printLineWithSpacing('    ' + menuItem.menuData.productName.toUpperCase(), getOptions(menuFormatOptions))
          printLineWithSpacing('    ∙' + menuItem.name, getOptions(menuFormatOptions))

          // Zakomentirano da se ne ispisuje opcije kraj svakog artikla, potrebno kasnije dodati
          // if (menuItem.tags && menuItem.tags.length > 0) {
          //   menuItem.tags.forEach((tag, tagIndex) => {
          //     if (tagIndex === 0) {
          //       printLine('Odabrane opcije', getOptions({ alignment: 0 }))
          //     }

          //     let allTags = ''
          //     if (tag.tagItems && tag.tagItems.length > 0) {
          //       tag.tagItems.forEach((tagOption, key) => {
          //         if (key === 0) {
          //           allTags += ' ' + tagOption
          //         } else {
          //           allTags += ', ' + tagOption
          //         }
          //       })
          //     }
          //     printLine(`${tag.tagName}: ${allTags}`, getOptions({ alignment: 0 }))
          //   })
          // }
        })

        if (menus[key].tags && menus[key].tags.length > 0) {
          menus[key].tags.forEach((tag, tagIndex) => {
            if (tag.tagItems && tag.tagItems.length > 0) {
              printLine(`    ${tag.tagName.toUpperCase()}`, options)

              tag.tagItems.forEach((tagOption) => {
                printLine('    ∙' + tagOption, options)
              })
            }
          })
        }
      }
    })
  }

  var printOtherUnitsArray = []
  if (otherUnits && otherUnits.length > 0) {
    // Add spacing before separator to separate from previous content
    const footerOptions = getOptionsFromFormat(printFormat.footer, options, printerLib)
    printLineWithSpacing('', getOptions(footerOptions))
    printSeparator(options)
    otherUnits.forEach(unit => {
      if (unit.id !== orgUnit.id) {
        printOtherUnitsArray.push(unit.name)
      }
    })

    if (printOtherUnitsArray && printOtherUnitsArray.length > 0) {
      printLineWithSpacing('Ostatak narudžbe u:', getOptions(getOptionsFromFormat(printFormat.footer, options, printerLib)))
      printOtherUnitsArray.forEach(unitName => {
        printLineWithSpacing(' - ' + unitName, getOptions(getOptionsFromFormat(printFormat.footer, options, printerLib)))
      })
      printLine('\n', options)
    }
  }

  if (note) {
    // Add spacing before separator to separate from previous content
    const footerOptions = getOptionsFromFormat(printFormat.footer, options, printerLib)
    printLineWithSpacing('', getOptions(footerOptions))
    printSeparator(options)
    printLineWithSpacing(`- ${note.message}`, getOptions(getOptionsFromFormat(printFormat.footer, options, printerLib)))
  }

  printLine('\n'.repeat(2), options)
  /* TODO: remove */

  try {
    console.info(`Printing order on ${printerConfiguration.library}`)
    if (printerConfiguration.library === 'UNIVERSAL') {
      doc.cut()
      var escposCommands = options.document.generateUInt8Array()

      // create ClientPrintJob
      var cpj = new JSPM.ClientPrintJob()

      // Set Printer info
      // const myPrinter = new JSPM.NetworkPrinter(9100, '172.29.35.88')

      const printer = printerConfiguration

      let selectedPrinter
      if (printer.ip_address && printer.ip_address !== '') {
        if (printer.port && printer.port !== 9100) {
          selectedPrinter = new JSPM.NetworkPrinter(printer.port, printer.ip_address)
        } else {
          selectedPrinter = new JSPM.NetworkPrinter(9100, printer.ip_address)
        }
      } else {
        selectedPrinter = new JSPM.InstalledPrinter(printer.printer_name)
      }

      cpj.clientPrinter = selectedPrinter

      // Set the ESC/POS commands
      cpj.binaryPrinterCommands = escposCommands

      // Send print job to printer!
      try {
        await cpj.sendToClient()
        console.log('🟢 [order.js] Print job sent successfully')
        return { success: true }
      } catch (sendError) {
        console.error('🔴 [order.js] Error sending print job:', sendError)
        throw sendError // Re-throw to be caught by outer catch
      }
    } else if (state.getPrinterLib() === 'BIXOLON80') {
      cutPaper()
      var strSubmit = getPosData()
      // BIXOLON is async and will be converted to Promise in Phase 2
      await requestPrint(printerConfiguration.printer_name, strSubmit, viewResult, printerConfiguration.ip_address)
      return { success: true }
    }
  } catch (err) {
    console.error('Error printing order:', err)

    // Return failure status instead of true
    return { success: false, error: err.message || 'Unknown error' }
  }

  return { success: true }
}
